// Shared vocabulary for the sticky-note board and the design canvas.

// Sticky-note palette (paper + magnet-pin colour). Index into this array is
// what both Note.color and CanvasElement.paletteIndex store.
export const NOTE_STYLES = [
  { paper: "#c8f2d4", pin: "#2e7d32" }, // green
  { paper: "#fcd5d9", pin: "#c62828" }, // pink/red
  { paper: "#c6ecf7", pin: "#1565c0" }, // blue
  { paper: "#f7d3a8", pin: "#b45309" }, // orange
  { paper: "#f7f3a0", pin: "#f59e0b" }, // yellow
  { paper: "#e2d5f7", pin: "#6d28d9" }, // purple
  { paper: "#c7f0e9", pin: "#0f766e" }, // teal
  { paper: "#ffd6e7", pin: "#be185d" }, // rose
  { paper: "#d7dcff", pin: "#4338ca" }, // indigo
  { paper: "#e2e8f0", pin: "#475569" }, // slate
  { paper: "#ffffff", pin: "#6d28d9" }, // white
];

export const NOTE_FONT = "'Comic Sans MS','Segoe Print',cursive,sans-serif";

// Default design surface, in canvas units (CSS px at 100% zoom).
export const DEFAULT_W = 1280;
export const DEFAULT_H = 800;

export const MIN_SIZE = 24; // smallest an element can be resized to

// Brand-aligned swatches for fills, text and backgrounds.
export const SWATCHES = [
  "#ff6b35",
  "#ff9066",
  "#fbbf24",
  "#22c55e",
  "#0ea5e9",
  "#6366f1",
  "#a855f7",
  "#ec4899",
  "#0f172a",
  "#475569",
  "#94a3b8",
  "#e2e8f0",
  "#ffffff",
  "transparent",
];

export const FONTS = [
  { label: "Inter", value: "Inter, 'Segoe UI', system-ui, sans-serif" },
  { label: "Georgia", value: "Georgia, 'Times New Roman', serif" },
  { label: "Handwriting", value: NOTE_FONT },
  { label: "Mono", value: "'Courier New', ui-monospace, monospace" },
  { label: "Impact", value: "Impact, 'Arial Black', sans-serif" },
];

export const BACKGROUNDS = [
  { kind: "grid", label: "Grid", color: "#ffffff", color2: "#e2e8f0" },
  { kind: "dots", label: "Dots", color: "#ffffff", color2: "#cbd5e1" },
  { kind: "solid", label: "Solid", color: "#ffffff", color2: "#ffffff" },
  { kind: "cork", label: "Cork", color: "#d8ac72", color2: "#b8894d" },
  { kind: "gradient", label: "Gradient", color: "#ff9066", color2: "#fff8f5" },
];

export const CANVAS_SIZES = [
  { label: "Landscape 1280×800", width: DEFAULT_W, height: DEFAULT_H },
  { label: "Square 1080×1080", width: 1080, height: 1080 },
  { label: "Portrait 1080×1350", width: 1080, height: 1350 },
  { label: "Slide 1920×1080", width: 1920, height: 1080 },
  { label: "Story 1080×1920", width: 1080, height: 1920 },
];

// Largest edge we downscale uploaded images to before embedding them as a
// data URL — keeps a saved design comfortably inside the 15mb body limit.
export const MAX_IMAGE_EDGE = 1400;

export const uid = () =>
  `${Date.now().toString(36)}${Math.random().toString(36).slice(2, 8)}`;

// Per-type defaults. Everything else falls back to the schema defaults.
const PRESETS = {
  note: { w: 210, h: 200, fill: "transparent", paletteIndex: 0 },
  text: {
    w: 320,
    h: 70,
    text: "Double-click to edit",
    fill: "transparent",
    color: "#0f172a",
    fontSize: 28,
    fontWeight: "700",
    align: "left",
  },
  rect: { w: 240, h: 160, fill: "#ff6b35", radius: 12 },
  ellipse: { w: 200, h: 200, fill: "#0ea5e9" },
  line: { w: 260, h: 0, fill: "transparent", stroke: "#0f172a", strokeWidth: 4 },
  image: { w: 320, h: 240, fill: "transparent" },
};

export const createElement = (type, overrides = {}) => ({
  id: uid(),
  type,
  x: 80,
  y: 80,
  w: 200,
  h: 150,
  rotation: 0,
  z: 0,
  text: "",
  src: "",
  fill: "#ff6b35",
  stroke: "transparent",
  strokeWidth: 0,
  radius: 0,
  opacity: 1,
  paletteIndex: 0,
  color: "#0f172a",
  fontSize: 18,
  fontFamily: FONTS[0].value,
  fontWeight: "600",
  align: "left",
  ...PRESETS[type],
  ...overrides,
});

// CSS background for the live DOM surface. `exportPng` draws the equivalent
// onto a 2D context — keep the two in step.
export const backgroundCss = (bg) => {
  const { kind, color, color2 } = bg;
  switch (kind) {
    case "solid":
      return { background: color };
    case "gradient":
      return { background: `linear-gradient(135deg, ${color} 0%, ${color2} 100%)` };
    case "grid":
      return {
        background: color,
        backgroundImage: `linear-gradient(${color2} 1px, transparent 1px), linear-gradient(90deg, ${color2} 1px, transparent 1px)`,
        backgroundSize: "40px 40px",
      };
    case "dots":
      return {
        background: color,
        backgroundImage: `radial-gradient(${color2} 2px, transparent 2px)`,
        backgroundSize: "28px 28px",
      };
    case "cork":
      return {
        background: color,
        backgroundImage: `radial-gradient(${color2} 1.5px, transparent 1.6px), radial-gradient(${color2} 1px, transparent 1.1px)`,
        backgroundSize: "22px 22px, 13px 13px",
        backgroundPosition: "0 0, 7px 9px",
      };
    default:
      return { background: "#ffffff" };
  }
};
